/**
 * useTutorStudentActivitiesReorder — le tuteur range la liste « À faire » de son élève.
 *
 * Le geste du legacy (`TodoListScreen.js:1183-1199`, branche tuteur) : la liste prend l'ordre posé tout
 * de suite (`setTodoActivities`), puis `PUT …/students/{id}/activities/todo/indexes`
 * (`activityTodoIndexesAsTutor`, `{ id }` en route, `{ todo_activities_ids }` en corps), dont la réponse
 * passe par le réducteur `currentUser`. Hors live, rien ne part à la salle (le `newTodo` du legacy est
 * gardé par `isLiveMode`).
 *
 * Le séquencement est celui de l'élève (`todoReorderStep`, testé) : deux rangements rapides ne se
 * marchent pas dessus, et un échec remet la liste à l'ordre que le serveur tient, dit en bas de l'onglet
 * — le legacy gardait l'ordre refusé à l'écran jusqu'à la relecture suivante.
 */
import { useCallback, useMemo, useRef, useState } from "react"
import { useDispatch, useStore } from "react-redux"

import { setTodoActivities } from "../../../../../../data/redux/actions/data"
import { activitiesService } from "../../../../../../data/services/activitiesService"
import { apiCall, apiFailureOf } from "../../../../../../engine/shared/apiCall"
import { createTelemetry } from "../../../../../../engine/shared/telemetry"
import { type ApiActivity } from "../../../../adapters/parent/Activities/activityAdapter"
import { reorderTodoActivities } from "../../../../adapters/student/Activities/studentActivitiesAdapter"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"
import { useGesture } from "../../../hooks/gestureTracking"
import {
    initialTodoReorderState,
    todoReorderStep,
    type TodoReorderEvent,
    type TodoReorderFailure,
} from "../../student/Activities/useStudentTodoReorder"
import { type TutorStudentId } from "./tutorStudentTabs"

const phaseOf = (state: typeof initialTodoReorderState) =>
    state.saving ? "saving" : state.failure != null ? "failed" : "idle"

export function useTutorStudentActivitiesReorder(studentId: TutorStudentId | null) {
    const dispatch = useDispatch()
    const store = useStore()
    const telemetry = useMemo(() => createTelemetry("activities-tutor.reorder", { role: "tutor" }), [])
    const gesture = useGesture("activity.reorder", "tutor_student_activities")
    const stateRef = useRef(initialTodoReorderState)
    const [failure, setFailure] = useState<TodoReorderFailure | null>(null)
    const studentIdRef = useRef(studentId)
    studentIdRef.current = studentId

    const run = useCallback(
        (event: TodoReorderEvent) => {
            const before = stateRef.current
            const { state, write, send } = todoReorderStep(before, event)
            stateRef.current = state
            if (phaseOf(before) !== phaseOf(state)) telemetry.transition(phaseOf(before), phaseOf(state), event.type)
            if (write != null) dispatch(setTodoActivities(write) as never)
            setFailure(state.failure)
            const id = studentIdRef.current
            if (send == null || id == null) return

            gesture.submitted()
            apiCall<ApiActivity[]>(dispatch as never, activitiesService.activityTodoIndexesAsTutor, {
                routeParams: { id },
                data: { todo_activities_ids: send.ids },
            })
                .then((list) => {
                    gesture.succeeded()
                    run({ type: "SAVED", seq: send.seq, list: Array.isArray(list) ? list : [] })
                })
                .catch((cause: unknown) => {
                    gesture.failed(apiFailureOf(cause) ?? cause)
                    const offline = apiFailureOf(cause)?.isOffline === true
                    if (!offline) telemetry.error("save", cause, { count: send.ids.length })
                    run({ type: "FAILED", seq: send.seq, failure: offline ? "offline" : "rejected" })
                })
        },
        [dispatch, gesture, telemetry]
    )

    const reorder = useCallback(
        (ids: (string | number)[]) => {
            const id = studentIdRef.current
            if (id == null) return
            const before = (selectDisplayedStudent(store.getState(), id)?.todo_activities ?? []) as ApiActivity[]
            const next = reorderTodoActivities(before, ids)
            // L'ordre ne décrit plus la liste (une relecture est passée sous le doigt) : rien ne part.
            if (next == null) return
            run({ type: "REORDERED", before, next })
        },
        [run, store]
    )

    const dismiss = useCallback(() => run({ type: "DISMISSED" }), [run])

    return { failure, reorder, dismiss }
}
